import { type DossierTachesApiObject } from '@europrocurement/l2d-domain/reducers/formalites/slices/dossierTachesSlice';
import { type PrescripteurTachesApiObject } from '@europrocurement/l2d-domain/reducers/tiers/slices/prescripteurTachesSlice';
import { dossiersBasePath } from '@europrocurement/l2d-modules/modules/Dossiers';
import { prescripteursBasePath } from '@europrocurement/l2d-modules/modules/PrescriberClient/constants/paths';
import CheckableTaskRow from '@europrocurement/l2d-modules/modules/Tasks/components/CheckableTaskRow/CheckableTaskRow';
import useTasksHook, {
    CallApiGetTachesParams,
} from '@europrocurement/l2d-modules/modules/Tasks/hook/useTasksHook';
import { faFolder } from '@fortawesome/sharp-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, SxProps, Typography, useTheme } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { HeaderActionButton } from '@europrocurement/flexy-components';
import { addTaskIcon } from '@europrocurement/l2d-icons';
import { Link } from 'react-router-dom';

type TasksWidgetDashboardProps = {
    title: string;
    filters: CallApiGetTachesParams;
    apiTarget?: 'formalite' | 'tiers' | 'utilisateur';
    sxProps?: SxProps;
    onTasksLoaded?: (count: number) => void;
    onAddTask?: () => void;
};

const TasksWidgetDashboard: React.FunctionComponent<TasksWidgetDashboardProps> = function ({
    apiTarget = 'formalite',
    filters,
    title,
    sxProps,
    onTasksLoaded,
    onAddTask,
}) {
    const { palette } = useTheme();
    const outerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState<number>(9999);
    const isMini = containerWidth < 280;

    useEffect(() => {
        const el = outerRef.current;
        if (!el) return undefined;
        const observer = new ResizeObserver(([entry]) => {
            setContainerWidth(entry.contentRect.width);
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const {
        tasksLoading,
        orderedTasks,
        loadTasks,
        handleCheckTask,
        handleEditTask,
        handleUpdatePriority,
        handleDeleteTask,
    } = useTasksHook({ autoload: false, apiTarget });

    // Load task filtered
    useEffect(() => {
        if (!tasksLoading && filters) {
            loadTasks(filters);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    useEffect(() => {
        if (!tasksLoading) {
            onTasksLoaded?.(orderedTasks.length);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tasksLoading, orderedTasks.length]);

    return (
        <Box
            ref={outerRef}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                padding: '8px 0',
                ...sxProps,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: isMini ? 'column' : 'row',
                    alignItems: isMini ? 'flex-start' : 'center',
                    justifyContent: 'space-between',
                    gap: isMini ? '4px' : 0,
                }}
            >
                {isMini && onAddTask && (
                    <HeaderActionButton
                        label="Ajouter une tâche"
                        icon={addTaskIcon}
                        onClick={onAddTask}
                        sx={{ paddingLeft: '10px', paddingRight: '10px' }}
                    />
                )}
                <Typography variant="l2dh6">{title}</Typography>
                {!isMini && onAddTask && (
                    <HeaderActionButton
                        label="Ajouter une tâche"
                        icon={addTaskIcon}
                        onClick={onAddTask}
                        sx={{ paddingLeft: '10px', paddingRight: '10px' }}
                    />
                )}
            </Box>
            {orderedTasks.length === 0 && !tasksLoading ? (
                <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary', fontStyle: 'italic', padding: '4px 0' }}
                >
                    {apiTarget === 'utilisateur' && 'Aucune tâche personnelle'}
                    {apiTarget === 'formalite' && 'Aucune tâche dossier'}
                    {apiTarget === 'tiers' && 'Aucune tâche prescripteur'}
                </Typography>
            ) : (
                orderedTasks.map((task) => (
                    <CheckableTaskRow
                        key={`tache_${task.id}`}
                        test-id="checkable-task"
                        task={task}
                        handleCheckTask={() => handleCheckTask(task)}
                        onEditTask={() => handleEditTask(task)}
                        canDeleteTask={false}
                        onDeleteTask={() => handleDeleteTask(task.id as number)}
                        onPriorityChange={(t, newPriority) => handleUpdatePriority(t, newPriority)}
                        linkToEntity={
                            apiTarget !== 'utilisateur' ? (
                                <Box
                                    sx={{
                                        border: `1px solid ${palette.primary.main}`,
                                        borderRadius: '8px',
                                        padding: '4px 8px',
                                        textDecoration: 'none',
                                        color: palette.primary.dark,
                                        transition: 'all ease-in-out 300ms',
                                        '&:hover': { color: palette.primary.main },
                                        fontSize: '12px',
                                    }}
                                >
                                    {apiTarget === 'formalite' && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                maxWidth: '100px',
                                                overflowX: 'hidden',
                                                gap: '4px',
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faFolder} />
                                            <Link
                                                style={{
                                                    textDecoration: 'none',
                                                    color: 'unset',
                                                    textWrap: 'nowrap',
                                                }}
                                                to={`/${dossiersBasePath}/${(task as DossierTachesApiObject).dossier.id}?dossier-detail=activities`}
                                                target="_blank"
                                            >
                                                <Typography variant="l2dcaption">
                                                    #
                                                    {
                                                        (task as DossierTachesApiObject).dossier
                                                            .numero
                                                    }
                                                </Typography>
                                            </Link>
                                        </Box>
                                    )}
                                    {apiTarget === 'tiers' && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                maxWidth: '120px',
                                                overflowX: 'hidden',
                                                gap: '4px',
                                            }}
                                        >
                                            <FontAwesomeIcon icon={['fasl', 'user']} />
                                            <Link
                                                style={{
                                                    textDecoration: 'none',
                                                    color: 'unset',
                                                    textWrap: 'nowrap',
                                                }}
                                                to={`/${prescripteursBasePath}/liste/${(task as PrescripteurTachesApiObject).tiers.id}`}
                                                target="_blank"
                                            >
                                                <Typography variant="l2dcaption">
                                                    {
                                                        (task as PrescripteurTachesApiObject).tiers
                                                            .raisonSociale
                                                    }
                                                </Typography>
                                            </Link>
                                        </Box>
                                    )}
                                </Box>
                            ) : (
                                // eslint-disable-next-line react/jsx-no-useless-fragment
                                <></>
                            )
                        }
                    />
                ))
            )}
        </Box>
    );
};

export default TasksWidgetDashboard;
